import React, { useState, forwardRef, useImperativeHandle } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { TreeNode } from "@/components/ui/TreeNode";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { useTheme } from "@/hooks/useTheme";
import { CourseSettingsCard } from "./CourseSettingsCard";
import { SectionCard } from "./SectionCard";
import type { CourseMetaType, SectionType, QuestionType, CourseStatusType } from "@/types/db/courses";
import { theme } from "@/constants/themes";

// Status options for dropdown
const STATUS_OPTIONS = [
  { text: "DRAFT", color: "pending" },
  { text: "ACTIVE", color: "complete" },
] as const;

export function getCourseStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    DRAFT: colors.pending.border,
    ACTIVE: colors.complete.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export const COURSE_STATUS_OPTIONS = STATUS_OPTIONS;

export interface CourseCreateFormProps {
  initialMetaData?: CourseMetaType;
  onDirtyChange?: (dirty: boolean) => void;
}

export interface CourseCreateFormRef {
  getMetaData: () => CourseMetaType;
  getFormData: () => { description: string; status: string; openEnrollment: boolean };
  resetForm: () => void;
  isDirty: () => boolean;
}

const defaultMetaData: CourseMetaType = {
  settings: {
    allowRetry: true,
    maxAttempts: null,
    showCorrectAnswers: false,
    showCorrectAnswersAfter: "completion",
    passingScore: 70,
    timeLimit: null,
    shuffleQuestions: false,
    shuffleOptions: false,
    certificateEnabled: false,
  },
  sections: [],
  grading: {
    method: "percentage",
    sectionWeights: {},
    passingScore: 70,
  },
};

export const CourseCreateForm = forwardRef<CourseCreateFormRef, CourseCreateFormProps>(
  ({ initialMetaData = defaultMetaData, onDirtyChange }, ref) => {
    const { colors } = useTheme();

    // Form state
    const [metaData, setMetaData] = useState<CourseMetaType>(initialMetaData);
    const [description, setDescription] = useState("");
    const [status, setStatus] = useState<CourseStatusType>("DRAFT");
    const [openEnrollment, setOpenEnrollment] = useState(false);

    // Track if form has been modified
    const [dirty, setDirty] = useState(false);

    const markDirty = () => {
      if (!dirty) {
        setDirty(true);
        onDirtyChange?.(true);
      }
    };

    // Expose imperative methods to parent
    useImperativeHandle(ref, () => ({
      getMetaData: () => metaData,
      getFormData: () => ({ description, status, openEnrollment }),
      resetForm: () => {
        setMetaData(initialMetaData);
        setDescription("");
        setStatus("DRAFT");
        setOpenEnrollment(false);
        setDirty(false);
        onDirtyChange?.(false);
      },
      isDirty: () => dirty,
    }));

    // Update metadata settings
    const updateMetaData = (updates: Partial<CourseMetaType>) => {
      markDirty();
      setMetaData((prev) => ({ ...prev, ...updates }));
    };

    // Section handlers
    const addSection = () => {
      markDirty();
      const newSection: SectionType = {
        id: crypto.randomUUID(),
        title: "",
        description: "",
        order: metaData.sections.length,
        settings: {},
        questions: [],
        requiredToPass: true,
      };
      setMetaData((prev) => ({
        ...prev,
        sections: [...prev.sections, newSection],
      }));
    };

    const updateSection = (sectionIndex: number, updates: Partial<SectionType>) => {
      markDirty();
      setMetaData((prev) => {
        const newSections = [...prev.sections];
        newSections[sectionIndex] = { ...newSections[sectionIndex], ...updates };
        return { ...prev, sections: newSections };
      });
    };

    const deleteSection = (sectionIndex: number) => {
      markDirty();
      setMetaData((prev) => ({
        ...prev,
        sections: prev.sections.filter((_, i) => i !== sectionIndex),
      }));
    };

    // Question handlers
    const addQuestion = (sectionIndex: number, questionType: QuestionType["type"] = "single") => {
      markDirty();
      let newQuestion: QuestionType;

      switch (questionType) {
        case "single":
          newQuestion = {
            id: crypto.randomUUID(),
            prompt: "",
            media: [],
            skippable: false,
            points: 1,
            tags: [],
            type: "single",
            options: [
              { id: `opt-${Date.now()}-1`, text: "" },
              { id: `opt-${Date.now()}-2`, text: "" },
            ],
            correct: "",
          };
          break;
        case "multiple":
          newQuestion = {
            id: crypto.randomUUID(),
            prompt: "",
            media: [],
            skippable: false,
            points: 1,
            tags: [],
            type: "multiple",
            options: [
              { id: `opt-${Date.now()}-1`, text: "" },
              { id: `opt-${Date.now()}-2`, text: "" },
            ],
            correct: [],
          };
          break;
        case "true_false":
          newQuestion = {
            id: crypto.randomUUID(),
            prompt: "",
            media: [],
            skippable: false,
            points: 1,
            tags: [],
            type: "true_false",
            correct: true,
          };
          break;
        case "short":
          newQuestion = {
            id: crypto.randomUUID(),
            prompt: "",
            media: [],
            skippable: false,
            points: 1,
            tags: [],
            type: "short",
            acceptable: [""],
            caseSensitive: false,
            normalizeWhitespace: true,
          };
          break;
        case "ordering":
          newQuestion = {
            id: crypto.randomUUID(),
            prompt: "",
            media: [],
            skippable: false,
            points: 1,
            tags: [],
            type: "ordering",
            items: [
              { id: `item-${Date.now()}-1`, text: "" },
              { id: `item-${Date.now()}-2`, text: "" },
            ],
            correct: [],
          };
          break;
        case "matching":
          newQuestion = {
            id: crypto.randomUUID(),
            prompt: "",
            media: [],
            skippable: false,
            points: 1,
            tags: [],
            type: "matching",
            left: [{ id: `left-${Date.now()}-1`, text: "" }],
            right: [{ id: `right-${Date.now()}-1`, text: "" }],
            correct: [],
          };
          break;
      }

      setMetaData((prev) => {
        const newSections = [...prev.sections];
        newSections[sectionIndex] = {
          ...newSections[sectionIndex],
          questions: [...newSections[sectionIndex].questions, newQuestion],
        };
        return { ...prev, sections: newSections };
      });
    };

    const updateQuestion = (sectionIndex: number, questionIndex: number, updates: Partial<QuestionType>) => {
      markDirty();
      setMetaData((prev) => {
        const newSections = [...prev.sections];
        const newQuestions = [...newSections[sectionIndex].questions];
        newQuestions[questionIndex] = { ...newQuestions[questionIndex], ...updates } as QuestionType;
        newSections[sectionIndex] = { ...newSections[sectionIndex], questions: newQuestions };
        return { ...prev, sections: newSections };
      });
    };

    const deleteQuestion = (sectionIndex: number, questionIndex: number) => {
      markDirty();
      setMetaData((prev) => {
        const newSections = [...prev.sections];
        newSections[sectionIndex] = {
          ...newSections[sectionIndex],
          questions: newSections[sectionIndex].questions.filter((_, i) => i !== questionIndex),
        };
        return { ...prev, sections: newSections };
      });
    };

    const changeQuestionType = (sectionIndex: number, questionIndex: number, newType: QuestionType["type"]) => {
      markDirty();
      setMetaData((prev) => {
        const newSections = [...prev.sections];
        const oldQuestion = newSections[sectionIndex].questions[questionIndex];

        // Preserve common fields from old question
        const baseQuestion = {
          id: oldQuestion.id,
          prompt: oldQuestion.prompt,
          media: oldQuestion.media || [],
          skippable: oldQuestion.skippable,
          points: oldQuestion.points,
          tags: oldQuestion.tags || [],
        };

        // Create new question with the new type
        let newQuestion: QuestionType;
        switch (newType) {
          case "single":
            newQuestion = {
              ...baseQuestion,
              type: "single",
              options: [
                { id: `opt-${Date.now()}-1`, text: "" },
                { id: `opt-${Date.now()}-2`, text: "" },
              ],
              correct: "",
            };
            break;
          case "multiple":
            newQuestion = {
              ...baseQuestion,
              type: "multiple",
              options: [
                { id: `opt-${Date.now()}-1`, text: "" },
                { id: `opt-${Date.now()}-2`, text: "" },
              ],
              correct: [],
            };
            break;
          case "true_false":
            newQuestion = {
              ...baseQuestion,
              type: "true_false",
              correct: true,
            };
            break;
          case "short":
            newQuestion = {
              ...baseQuestion,
              type: "short",
              acceptable: [""],
              caseSensitive: false,
              normalizeWhitespace: true,
            };
            break;
          case "ordering":
            newQuestion = {
              ...baseQuestion,
              type: "ordering",
              items: [
                { id: `item-${Date.now()}-1`, text: "" },
                { id: `item-${Date.now()}-2`, text: "" },
              ],
              correct: [],
            };
            break;
          case "matching":
            newQuestion = {
              ...baseQuestion,
              type: "matching",
              left: [{ id: `left-${Date.now()}-1`, text: "" }],
              right: [{ id: `right-${Date.now()}-1`, text: "" }],
              correct: [],
            };
            break;
        }

        const newQuestions = [...newSections[sectionIndex].questions];
        newQuestions[questionIndex] = newQuestion;
        newSections[sectionIndex] = { ...newSections[sectionIndex], questions: newQuestions };
        return { ...prev, sections: newSections };
      });
    };

    return (
      <View style={styles.container}>
        {/* Course Basic Info */}
        <View style={styles.basicInfo}>
          {/* Description */}
          <View style={{ gap: 8 }}>
            <ThemedText type="defaultSemiBold">Description</ThemedText>
            <ThemedInput
              testID="course-description-input"
              placeholder="Describe what this course covers..."
              value={description}
              onChangeText={(v) => {
                setDescription(v);
                markDirty();
              }}
              multiline
              numberOfLines={4}
              inputStyle={{ minHeight: 100, textAlignVertical: "top" }}
            />
          </View>

          {/* Status */}
          <View style={{ gap: 8 }}>
            <ThemedText type="defaultSemiBold">Status</ThemedText>
            <View style={styles.statusRow}>
              <Pressable
                testID="course-status-draft"
                onPress={() => {
                  setStatus("DRAFT");
                  markDirty();
                }}
                style={[
                  styles.statusButton,
                  {
                    backgroundColor: status === "DRAFT" ? colors.primary : colors.backgroundSecondary,
                    borderColor: status === "DRAFT" ? colors.primary : colors.border,
                  },
                ]}
              >
                <ThemedText style={{ color: status === "DRAFT" ? "white" : colors.text }}>Draft</ThemedText>
              </Pressable>
              <Pressable
                testID="course-status-active"
                onPress={() => {
                  setStatus("ACTIVE");
                  markDirty();
                }}
                style={[
                  styles.statusButton,
                  {
                    backgroundColor: status === "ACTIVE" ? colors.primary : colors.backgroundSecondary,
                    borderColor: status === "ACTIVE" ? colors.primary : colors.border,
                  },
                ]}
              >
                <ThemedText style={{ color: status === "ACTIVE" ? "white" : colors.text }}>Active</ThemedText>
              </Pressable>
            </View>
          </View>

          {/* Open Enrollment */}
          <View style={styles.toggleRow}>
            <View style={{ flex: 1 }}>
              <ThemedText type="defaultSemiBold">Open Enrollment</ThemedText>
              <ThemedText style={styles.helperText}>Allow users to self-enroll in this course</ThemedText>
            </View>
            <ToggleSwitch
              value={openEnrollment}
              onValueChange={(v) => {
                setOpenEnrollment(v);
                markDirty();
              }}
              testID="course-open-enrollment-toggle"
            />
          </View>
        </View>

        {/* Course Settings */}
        <CourseSettingsCard metaData={metaData} onUpdate={updateMetaData} />

        {/* Sections */}
        <View style={styles.section}>
          <ThemedText type="subtitle" style={{ marginBottom: 8 }}>
            Course Sections
          </ThemedText>
          {metaData.sections.length === 0 && (
            <View style={[styles.emptyState, { borderColor: colors.border }]}>
              <ThemedText style={styles.helperText}>No sections yet. Add your first section to get started.</ThemedText>
            </View>
          )}
          {metaData.sections.map((section, sectionIndex) => (
            <SectionCard
              key={section.id}
              section={section}
              sectionIndex={sectionIndex}
              isLast={sectionIndex === metaData.sections.length - 1}
              onUpdate={(updates) => updateSection(sectionIndex, updates)}
              onDelete={() => deleteSection(sectionIndex)}
              onAddQuestion={() => addQuestion(sectionIndex)}
              onUpdateQuestion={(questionIndex, updates) => updateQuestion(sectionIndex, questionIndex, updates)}
              onDeleteQuestion={(questionIndex) => deleteQuestion(sectionIndex, questionIndex)}
              onChangeQuestionType={(questionIndex, newType) =>
                changeQuestionType(sectionIndex, questionIndex, newType)
              }
            />
          ))}

          {/* Add Section Button */}
          <TreeNode depth={0} isLast={true} isAddButton>
            <Pressable onPress={addSection} style={styles.addButton} testID="add-section-button">
              <ThemedText style={styles.addButtonText}>+ Add Section</ThemedText>
            </Pressable>
          </TreeNode>
        </View>
      </View>
    );
  },
);

CourseCreateForm.displayName = "CourseCreateForm";

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 16,
  },
  basicInfo: {
    gap: 16,
  },
  section: {
    gap: 8,
  },
  helperText: {
    fontSize: 12,
    opacity: 0.7,
  },
  emptyState: {
    padding: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: "dashed",
    alignItems: "center",
    justifyContent: "center",
  },
  addButton: {
    padding: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  addButtonText: {
    fontSize: 14,
    opacity: 0.7,
  },
  statusRow: {
    flexDirection: "row",
    gap: 8,
  },
  statusButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
});
